"use client";

import * as React from "react";
import { useCallback, useState } from "react";

import { cn } from "@/lib/utils";
import { LeadCaptureModal } from "@/components/lead-capture-modal";

const GRADIENT_COLORS = [
  "#00d47e",
  "#00f0a0",
  "#6366f1",
  "#a855f7",
  "#ec4899",
  "#f59e0b",
  "#00d47e",
].join(", ");

const SPIN_DURATION_MS = 4000;

// Detect @property support and register once at module level (client only).
// Browsers without support get a rAF fallback that drives --btn-angle via JS.
const needsFallback =
  typeof CSS === "undefined" || !("registerProperty" in CSS);

if (!needsFallback) {
  try {
    CSS.registerProperty({
      name: "--btn-angle",
      syntax: "<angle>",
      initialValue: "0deg",
      inherits: false,
    });
  } catch {
    // Already registered — fine
  }
}

// All border-span styles live in CSS to avoid SSR hydration mismatches
// from inline shorthand expansion.
const BORDER_STYLES = `
@property --btn-angle {
  syntax: "<angle>";
  initial-value: 0deg;
  inherits: false;
}
@keyframes borderSpin {
  to { --btn-angle: 360deg; }
}
.book-demo-border {
  padding: 1.5px;
  background: conic-gradient(from var(--btn-angle, 0deg), ${GRADIENT_COLORS});
  -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  animation: borderSpin 4s linear infinite;
}`;

interface BookDemoButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  children?: React.ReactNode;
}

export function BookDemoButton({
  children = "Book a Demo",
  className,
  ...props
}: BookDemoButtonProps) {
  const [open, setOpen] = useState(false);

  // React 19 callback ref with cleanup — starts/stops rAF on mount/unmount.
  // Only activates when the browser doesn't support @property (CSS animation
  // won't animate --btn-angle without it, so JS drives it instead).
  const borderRef = useCallback((el: HTMLSpanElement | null) => {
    if (!needsFallback || !el) return;

    // Disable the CSS animation that can't work without @property
    el.style.animationName = "none";

    let start: number | null = null;
    let raf: number;

    function animate(timestamp: number) {
      start ??= timestamp;
      const progress =
        ((timestamp - start) % SPIN_DURATION_MS) / SPIN_DURATION_MS;
      el!.style.setProperty("--btn-angle", `${progress * 360}deg`);
      raf = requestAnimationFrame(animate);
    }

    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: BORDER_STYLES }} />
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          "relative isolate inline-flex cursor-pointer items-center gap-2 rounded-full border-none px-9 py-3.5 text-[0.9375rem] font-bold transition-all duration-300",
          "bg-[#08090c] text-white",
          "hover:-translate-y-0.5 hover:shadow-[0_4px_30px_rgba(0,212,126,0.1)]",
          className,
        )}
        {...props}
      >
        <span
          ref={borderRef}
          aria-hidden
          className="book-demo-border pointer-events-none absolute inset-0 -z-10 rounded-full"
        />
        {children}
      </button>
      <LeadCaptureModal open={open} onOpenChange={setOpen} />
    </>
  );
}
